/**
 * PDP Same Day shipping module (AU).
 * localStorage key: sabo_delivery_location — shared with cart drawer / S&R later.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'sabo_delivery_location';
  var LOCATION_EVENT = 'sabo:delivery-location-changed';
  var AVAILABILITY_EVENT = 'sabo:availability-updated';
  var FETCH_TIMEOUT_MS = 3000;
  var TICK_MS = 30000;

  function sharedStore() {
    if (!window.SaboDelivery) {
      window.SaboDelivery = { location: null, availabilityByProduct: {} };
    }
    if (!window.SaboDelivery.availabilityByProduct) {
      window.SaboDelivery.availabilityByProduct = {};
    }
    return window.SaboDelivery;
  }
  var BRISBANE_OFFSET_MS = 10 * 60 * 60 * 1000;
  var WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var MONTH_SHORT = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  function qs(root, sel) {
    return root.querySelector(sel);
  }

  function qsa(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }

  function readStorage() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (!data || !data.postcode) return null;
      return {
        postcode: String(data.postcode).trim(),
        suburb: data.suburb ? String(data.suburb).trim() : ''
      };
    } catch (_e) {
      return null;
    }
  }

  function writeStorage(postcode, suburb, state) {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          postcode: String(postcode || '').trim(),
          suburb: String(suburb || '').trim(),
          state: String(state || '').trim(),
          fetchedAt: Date.now()
        })
      );
    } catch (_e) {
      /* ignore */
    }
  }

  function clearStorage() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (_e) {
      /* ignore */
    }
  }

  function padPostcode(value) {
    var digits = String(value || '').replace(/\D/g, '');
    if (!digits) return '';
    return digits.slice(0, 4).padStart(4, '0');
  }

  function titleCaseSuburb(value) {
    var s = String(value || '').trim();
    if (!s) return '';
    return s
      .toLowerCase()
      .split(/\s+/)
      .map(function (word) {
        if (!word) return word;
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
  }

  function parseMinutes(orderBy) {
    var parts = String(orderBy || '').split(':');
    if (parts.length < 2) return null;
    var h = parseInt(parts[0], 10);
    var m = parseInt(parts[1], 10);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return h * 60 + m;
  }

  function isEveningWindow(win) {
    var code = String((win && win.code) || '').toUpperCase();
    var slot = String((win && win.window) || '').toLowerCase();
    return code.indexOf('TON') >= 0 || slot.indexOf('5-9') >= 0 || slot.indexOf('5–9') >= 0;
  }

  // Compact delivery-time range, e.g. "1–5 PM" / "5–9 PM".
  function windowTimeRange(win) {
    var slug = String((win && win.window) || '')
      .toLowerCase()
      .replace(/–/g, '-')
      .replace(/\s+/g, '');
    if (slug.indexOf('5-9') >= 0) return '5–9 PM';
    if (slug.indexOf('1-5') >= 0) return '1–5 PM';
    var label = String((win && win.label) || '');
    var match = label.match(/(\d+)\s*[–-]\s*(\d+)\s*(AM|PM)/i);
    if (match) return match[1] + '–' + match[2] + ' ' + match[3].toUpperCase();
    return isEveningWindow(win) ? '5–9 PM' : '1–5 PM';
  }

  function parseIsoDateParts(iso) {
    var match = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return null;
    var year = parseInt(match[1], 10);
    var month = parseInt(match[2], 10);
    var day = parseInt(match[3], 10);
    return {
      year: year,
      month: month,
      day: day,
      weekday: new Date(Date.UTC(year, month - 1, day)).getUTCDay()
    };
  }

  // Day + date label with Today/Tomorrow shortcuts (Australia/Brisbane):
  // "Today, 4 Aug", "Tomorrow, 5 Aug", otherwise abbreviated "Tue 4 Aug".
  function windowDayDate(dateIso) {
    var parts = parseIsoDateParts(dateIso);
    if (!parts) return '';
    var dm = parts.day + ' ' + MONTH_SHORT[parts.month - 1];
    var now = brisbaneNowParts();
    if (parts.year === now.year && parts.month === now.month && parts.day === now.day) {
      return 'Today, ' + dm;
    }
    var tomorrow = addCalendarDays(now, 1);
    if (parts.year === tomorrow.year && parts.month === tomorrow.month && parts.day === tomorrow.day) {
      return 'Tomorrow, ' + dm;
    }
    return WEEKDAY_SHORT[parts.weekday] + ' ' + dm;
  }

  // Full window line in checkout style: "Tomorrow, 4 Aug · 1–5 PM".
  function windowLine(win) {
    var dd = windowDayDate(win && win.date);
    var range = windowTimeRange(win);
    return dd ? dd + ' · ' + range : range;
  }

  function formatCountdownLeft(remaining) {
    if (remaining == null || remaining <= 0) return '';
    if (remaining >= 60) {
      var h = Math.floor(remaining / 60);
      var m = remaining % 60;
      return m ? h + 'h ' + m + 'm left' : h + 'h left';
    }
    return remaining + ' min left';
  }

  function brisbaneNowParts() {
    var formatter = new Intl.DateTimeFormat('en-AU', {
      timeZone: 'Australia/Brisbane',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      weekday: 'short'
    });
    var parts = formatter.formatToParts(new Date());
    var map = {};
    parts.forEach(function (p) {
      map[p.type] = p.value;
    });
    return {
      year: parseInt(map.year, 10),
      month: parseInt(map.month, 10),
      day: parseInt(map.day, 10)
    };
  }

  function addCalendarDays(parts, days) {
    var d = brisbaneDateFromParts(parts);
    d = new Date(d.getTime() + days * 24 * 60 * 60 * 1000);
    var shifted = new Date(d.getTime() + BRISBANE_OFFSET_MS);
    return {
      year: shifted.getUTCFullYear(),
      month: shifted.getUTCMonth() + 1,
      day: shifted.getUTCDate(),
      weekday: shifted.getUTCDay()
    };
  }

  function brisbaneDateFromParts(parts) {
    return new Date(Date.UTC(parts.year, parts.month - 1, parts.day) - BRISBANE_OFFSET_MS);
  }

  function addBusinessDays(parts, days) {
    var d = brisbaneDateFromParts(parts);
    var left = Math.max(0, days | 0);
    while (left > 0) {
      d = new Date(d.getTime() + 24 * 60 * 60 * 1000);
      var wd = new Date(d.getTime() + BRISBANE_OFFSET_MS).getUTCDay();
      if (wd !== 0 && wd !== 6) left -= 1;
    }
    var shifted = new Date(d.getTime() + BRISBANE_OFFSET_MS);
    return {
      year: shifted.getUTCFullYear(),
      month: shifted.getUTCMonth() + 1,
      day: shifted.getUTCDate(),
      weekday: shifted.getUTCDay()
    };
  }

  function formatDayPart(p) {
    return WEEKDAY_SHORT[p.weekday] + ' ' + p.day;
  }

  function formatArrivesRange(minDays, maxDays) {
    var now = brisbaneNowParts();
    var start = addBusinessDays(now, minDays);
    var end = addBusinessDays(now, maxDays);
    var sameMonth = start.month === end.month;
    var left = formatDayPart(start);
    var right = formatDayPart(end) + ' ' + MONTH_SHORT[end.month - 1];
    if (!sameMonth) left += ' ' + MONTH_SHORT[start.month - 1];
    return 'Arrives ' + left + ' to ' + right;
  }

  function remainingMinutes(serverTimeIso, orderBy, windowDateIso, fetchedAtMs) {
    if (!serverTimeIso || !orderBy) return null;
    var serverMs = Date.parse(serverTimeIso);
    if (Number.isNaN(serverMs)) return null;

    var parts = String(orderBy || '').trim().split(':');
    var hh = parseInt(parts[0], 10);
    var mm = parseInt(parts[1] || '0', 10);
    if (Number.isNaN(hh) || Number.isNaN(mm)) return null;

    var dateParts = String(windowDateIso || '').split('-').map(function (n) {
      return parseInt(n, 10);
    });
    if (dateParts.length !== 3 || dateParts.some(function (n) { return Number.isNaN(n); })) {
      return null;
    }

    var y = dateParts[0];
    var mo = dateParts[1];
    var d = dateParts[2];
    var offsetMatch = String(serverTimeIso).match(/([+-]\d{2}):?(\d{2})$/);
    var offset =
      offsetMatch != null ? offsetMatch[1] + ':' + offsetMatch[2] : '+10:00';
    var cutoffIso =
      String(y).padStart(4, '0') +
      '-' +
      String(mo).padStart(2, '0') +
      '-' +
      String(d).padStart(2, '0') +
      'T' +
      String(hh).padStart(2, '0') +
      ':' +
      String(mm).padStart(2, '0') +
      ':00' +
      offset;
    var cutoffMs = Date.parse(cutoffIso);
    if (Number.isNaN(cutoffMs)) return null;
    var elapsedMs = Math.max(0, Date.now() - fetchedAtMs);
    return Math.floor((cutoffMs - (serverMs + elapsedMs)) / 60000);
  }

  function formatOrderByDisplay(orderBy) {
    var mins = parseMinutes(orderBy);
    if (mins == null) return orderBy;
    var h = Math.floor(mins / 60);
    var m = mins % 60;
    var suffix = h >= 12 ? 'PM' : 'AM';
    var h12 = h % 12;
    if (h12 === 0) h12 = 12;
    if (m === 0) return h12 + ' ' + suffix;
    return h12 + ':' + String(m).padStart(2, '0') + ' ' + suffix;
  }

  function PdpShipping(root) {
    this.root = root;
    this.productId = root.getAttribute('data-product-id') || '';
    this.availabilityUrl = (root.getAttribute('data-availability-url') || '').trim();
    this.excluded = root.getAttribute('data-same-day-excluded') === 'true';
    this.sameDayPrice = root.getAttribute('data-same-day-price') || '$14.95';
    this.sameDayTeaser = root.getAttribute('data-same-day-teaser') || 'Brisbane, Gold Coast & more';
    this.suburbPrompt = root.getAttribute('data-suburb-prompt') || 'Postcode [[pc]]: select your suburb';
    this.notServicedMessage = root.getAttribute('data-not-serviced-message') || 'Not available to [[pc]] yet';
    this.changeLabel = root.getAttribute('data-change-label') || 'Change';
    this.customerPostcode = root.getAttribute('data-customer-postcode') || '';
    this.customerSuburb = root.getAttribute('data-customer-suburb') || '';

    this.headerEl = qs(root, '[data-pdp-ship-header]');
    this.sameDayRow = qs(root, '[data-same-day-row]');
    this.brandEl = qs(root, '[data-pdp-ship-brand]');
    this.nameTextEl = qs(root, '[data-pdp-ship-name-text]');
    this.detailEl = qs(root, '[data-pdp-ship-detail]');
    this.priceEl = qs(root, '[data-pdp-ship-price]');
    this.openCheckBtn = qs(root, '[data-pdp-ship-open-check]');
    this.inlineCheck = qs(root, '[data-pdp-ship-inline-check]');
    this.postcodeInput = qs(root, '[data-pdp-ship-postcode]');
    this.checkBtn = qs(root, '[data-pdp-ship-check]');
    this.suburbWrap = qs(root, '[data-pdp-ship-suburb-wrap]');
    this.suburbSelect = qs(root, '[data-pdp-ship-suburb]');
    this.windowsEl = qs(root, '[data-pdp-ship-windows]');
    this.footerEl = qs(root, '[data-pdp-ship-footer]');
    this.windowSlots = qsa(root, '[data-window-slot]');
    this.statusEl = qs(root, '[data-pdp-ship-status]');
    this.srSummaryEl = qs(root, '[data-pdp-ship-sr-summary]');
    this.panelOpenBtn = qs(root, '[data-pdp-ship-panel-open]');

    this.instanceId = 'pdp-ship-' + Math.random().toString(36).slice(2);
    this.postcode = '';
    this.suburb = '';
    this.lastPayload = null;
    this.fetchedAt = 0;
    this.tickTimer = null;
    this.abortController = null;
    this.activeWindows = [];
    this.cutoffRefetchPending = false;

    this.fillStaticArrivals();
    this.bind();
    this.bindGlobalEvents();
    this.boot();
  }

  PdpShipping.prototype.fillStaticArrivals = function () {
    qsa(this.root, '[data-static-row]').forEach(function (row) {
      var min = parseInt(row.getAttribute('data-min-days') || '0', 10);
      var max = parseInt(row.getAttribute('data-max-days') || '0', 10);
      var el = qs(row, '[data-arrival-detail]');
      if (!el || Number.isNaN(min) || Number.isNaN(max)) return;
      var text = min + ' to ' + max + ' business days';
      if (row.getAttribute('data-static-row') === 'standard') text += ', free over $100';
      el.textContent = text;
    });
  };

  PdpShipping.prototype.bind = function () {
    var self = this;
    if (this.openCheckBtn) {
      this.openCheckBtn.addEventListener('click', function () {
        self.showInlineCheck();
      });
      this.openCheckBtn.addEventListener('focus', function () {
        self.showInlineCheck();
      });
    }
    if (this.checkBtn) {
      this.checkBtn.addEventListener('click', function () {
        self.onCheck();
      });
    }
    if (this.postcodeInput) {
      this.postcodeInput.addEventListener('keydown', function (event) {
        if (event.key === 'Enter') {
          event.preventDefault();
          self.onCheck();
        }
      });
    }
    if (this.suburbSelect) {
      this.suburbSelect.addEventListener('change', function () {
        var value = self.suburbSelect.value;
        if (!value) return;
        self.suburb = value;
        self.fetchAvailability(self.postcode, self.suburb);
      });
    }
    if (this.panelOpenBtn) {
      this.panelOpenBtn.addEventListener('click', function () {
        self.openPanel();
      });
    }
  };

  PdpShipping.prototype.bindGlobalEvents = function () {
    var self = this;
    document.addEventListener(LOCATION_EVENT, function (event) {
      var detail = (event && event.detail) || {};
      if (detail.source === self.instanceId) return;
      var pc = padPostcode(detail.postcode || '');
      var suburb = detail.suburb || '';
      if (!pc) {
        if (self.postcode) self.onChangeLocation({ silent: true });
        return;
      }
      if (pc === self.postcode && suburb === self.suburb) return;
      self.postcode = pc;
      self.suburb = suburb;
      if (self.postcodeInput) self.postcodeInput.value = pc;
      self.fetchAvailability(pc, suburb, { fromEvent: true });
    });
  };

  PdpShipping.prototype.openPanel = function () {
    var panel =
      document.querySelector('[data-shipping-returns-panel]') ||
      document.querySelector('shipping-returns-panel');
    if (panel && typeof panel.open === 'function') {
      panel.open(this.panelOpenBtn);
      return;
    }
    if (panel && typeof panel.showDialog === 'function') {
      panel.showDialog();
      return;
    }
    document.dispatchEvent(
      new CustomEvent('sabo:open-shipping-panel', { detail: { trigger: this.panelOpenBtn } })
    );
  };

  PdpShipping.prototype.broadcastLocation = function (postcode, suburb) {
    var store = sharedStore();
    store.location = postcode ? { postcode: postcode, suburb: suburb || '' } : null;
    document.dispatchEvent(
      new CustomEvent(LOCATION_EVENT, {
        detail: { postcode: postcode || '', suburb: suburb || '', source: this.instanceId }
      })
    );
  };

  PdpShipping.prototype.broadcastAvailability = function (data) {
    var store = sharedStore();
    if (this.productId) store.availabilityByProduct[this.productId] = data || null;
    document.dispatchEvent(
      new CustomEvent(AVAILABILITY_EVENT, {
        detail: {
          productId: this.productId,
          data: data || null,
          postcode: this.postcode,
          suburb: this.suburb
        }
      })
    );
  };

  PdpShipping.prototype.setStatus = function (text) {
    if (this.statusEl) this.statusEl.textContent = text || '';
  };

  PdpShipping.prototype.setLoading = function (on) {
    this.root.classList.toggle('is-loading', !!on);
    if (on) {
      this.setBrandMode(false);
      this.setDetail('Checking…', true);
    }
  };

  PdpShipping.prototype.setDetail = function (text, muted) {
    if (!this.detailEl) return;
    this.detailEl.textContent = text || '';
    this.detailEl.classList.toggle('is-muted-soft', muted !== false);
  };

  PdpShipping.prototype.setPrice = function (text, emphasize) {
    if (!this.priceEl) return;
    this.priceEl.textContent = text || '';
    this.priceEl.hidden = !text;
    this.priceEl.classList.toggle('is-emphasis', !!emphasize);
  };

  PdpShipping.prototype.setWashed = function (on) {
    if (this.sameDayRow) this.sameDayRow.classList.toggle('is-washed', !!on);
  };

  PdpShipping.prototype.setResolved = function (on) {
    if (this.sameDayRow) this.sameDayRow.classList.toggle('is-resolved', !!on);
  };

  PdpShipping.prototype.setBrandMode = function (_on) {
    // Always plain "Same Day Delivery" — no SABO wordmark.
    if (this.brandEl) this.brandEl.hidden = true;
    if (this.nameTextEl) this.nameTextEl.textContent = 'Same Day Delivery';
  };

  PdpShipping.prototype.clearWindows = function () {
    this.activeWindows = [];
    if (this.windowsEl) this.windowsEl.setAttribute('aria-hidden', 'true');
    this.windowSlots.forEach(function (slot) {
      slot.classList.remove('is-filled');
      var name = qs(slot, '[data-window-name]');
      var detail = qs(slot, '[data-window-detail]');
      var meta = qs(slot, '[data-window-meta]');
      if (name) name.textContent = '';
      if (detail) detail.textContent = '';
      if (meta) {
        meta.textContent = '';
        meta.classList.remove('is-countdown');
      }
    });
    if (this.footerEl) {
      this.footerEl.classList.remove('is-visible');
      this.footerEl.setAttribute('aria-hidden', 'true');
    }
  };

  PdpShipping.prototype.liveWindows = function (data, windows) {
    var self = this;
    var expired = false;
    var list = [];
    (windows || []).forEach(function (win) {
      if (!win) return;
      var remaining = remainingMinutes(
        data && data.serverTime,
        win.orderBy,
        win.date,
        self.fetchedAt
      );
      if (remaining != null && remaining <= 0) {
        expired = true;
        return;
      }
      list.push(win);
    });
    if (expired) this.scheduleCutoffRefetch();
    return list.slice(0, 2);
  };

  PdpShipping.prototype.scheduleCutoffRefetch = function () {
    if (this.cutoffRefetchPending || !this.postcode || !this.availabilityUrl) return;
    this.cutoffRefetchPending = true;
    this.fetchAvailability(this.postcode, this.suburb, { soft: true });
  };

  PdpShipping.prototype.countdownMeta = function (data, win) {
    var threshold = Number((data && data.countdownShowMinutes) || 60);
    if (Number.isNaN(threshold)) threshold = 60;
    var remaining = remainingMinutes(data && data.serverTime, win.orderBy, win.date, this.fetchedAt);

    if (remaining == null || remaining > threshold) {
      return { text: 'Order by ' + formatOrderByDisplay(win.orderBy), countdown: false };
    }
    if (remaining < 10) {
      return { text: Math.max(1, remaining) + ' min left', countdown: true };
    }
    return { text: formatCountdownLeft(remaining), countdown: true };
  };

  PdpShipping.prototype.renderWindows = function (data, windows) {
    var self = this;
    var list = this.liveWindows(data, windows);
    this.activeWindows = list;
    if (this.windowsEl) this.windowsEl.setAttribute('aria-hidden', list.length ? 'false' : 'true');

    this.windowSlots.forEach(function (slot, index) {
      var win = list[index];
      var nameEl = qs(slot, '[data-window-name]');
      var detailEl = qs(slot, '[data-window-detail]');
      var metaEl = qs(slot, '[data-window-meta]');
      if (!win) {
        slot.classList.remove('is-filled');
        if (nameEl) nameEl.textContent = '';
        if (detailEl) detailEl.textContent = '';
        if (metaEl) {
          metaEl.textContent = '';
          metaEl.classList.remove('is-countdown');
        }
        return;
      }
      slot.classList.add('is-filled');
      if (nameEl) nameEl.textContent = windowLine(win);
      if (detailEl) detailEl.textContent = '';
      if (metaEl) {
        var meta =
          index === 0
            ? self.countdownMeta(data, win)
            : { text: 'Order by ' + formatOrderByDisplay(win.orderBy), countdown: false };
        metaEl.textContent = meta.text;
        metaEl.classList.toggle('is-countdown', !!meta.countdown);
      }
    });

    var hasWindows = list.length > 0;
    if (this.footerEl) {
      this.footerEl.classList.toggle('is-visible', hasWindows);
      this.footerEl.setAttribute('aria-hidden', hasWindows ? 'false' : 'true');
    }
  };

  PdpShipping.prototype.hideActions = function () {
    if (this.openCheckBtn) this.openCheckBtn.hidden = true;
    if (this.inlineCheck) this.inlineCheck.hidden = true;
    if (this.suburbWrap) this.suburbWrap.hidden = true;
    if (this.detailEl) this.detailEl.hidden = false;
  };

  PdpShipping.prototype.showInlineCheck = function () {
    if (this.openCheckBtn) this.openCheckBtn.hidden = true;
    if (this.inlineCheck) this.inlineCheck.hidden = false;
    if (this.suburbWrap) this.suburbWrap.hidden = true;
    if (this.detailEl) this.detailEl.hidden = false;
    if (this.postcodeInput) {
      this.postcodeInput.focus();
      this.postcodeInput.select();
    }
  };

  PdpShipping.prototype.showOpenCheck = function () {
    if (this.openCheckBtn) this.openCheckBtn.hidden = false;
    if (this.inlineCheck) this.inlineCheck.hidden = true;
    if (this.suburbWrap) this.suburbWrap.hidden = true;
    if (this.detailEl) this.detailEl.hidden = false;
  };

  PdpShipping.prototype.boot = function () {
    if (this.excluded) {
      this.renderExcluded();
      return;
    }
    if (!this.availabilityUrl) {
      this.renderStateA();
      return;
    }

    var stored = readStorage();
    if (stored && stored.postcode) {
      this.postcode = padPostcode(stored.postcode);
      this.suburb = stored.suburb || '';
      if (this.postcodeInput) this.postcodeInput.value = this.postcode;
      this.fetchAvailability(this.postcode, this.suburb);
      return;
    }

    if (this.customerPostcode) {
      this.postcode = padPostcode(this.customerPostcode);
      this.suburb = this.customerSuburb || '';
      if (this.postcodeInput) this.postcodeInput.value = this.postcode;
      this.fetchAvailability(this.postcode, this.suburb);
      return;
    }

    this.renderStateA();
  };

  PdpShipping.prototype.onCheck = function () {
    if (this.excluded) return;
    var pc = padPostcode(this.postcodeInput ? this.postcodeInput.value : '');
    if (!pc || pc.length < 3) {
      this.showInlineCheck();
      return;
    }
    this.postcode = pc;
    this.suburb = '';
    if (this.suburbSelect) this.suburbSelect.value = '';
    this.fetchAvailability(this.postcode, '');
  };

  PdpShipping.prototype.onChangeLocation = function (options) {
    var silent = !!(options && options.silent);
    clearStorage();
    this.postcode = '';
    this.suburb = '';
    this.lastPayload = null;
    this.clearTick();
    if (this.postcodeInput) this.postcodeInput.value = '';
    this.renderStateA();
    this.setStatus('Enter a postcode to check Same Day delivery.');
    if (!silent) {
      this.broadcastLocation('', '');
      this.broadcastAvailability(null);
    }
  };

  PdpShipping.prototype.fetchAvailability = function (postcode, suburb, options) {
    var self = this;
    var soft = !!(options && options.soft);
    if (!this.availabilityUrl) {
      this.renderStateA();
      return;
    }

    if (this.abortController) this.abortController.abort();
    this.abortController = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timedOut = false;
    var timer = setTimeout(function () {
      timedOut = true;
      if (self.abortController) self.abortController.abort();
    }, FETCH_TIMEOUT_MS);

    this.cutoffRefetchPending = true;
    if (!soft) {
      this.setLoading(true);
      this.hideActions();
      this.clearWindows();
    }

    var url;
    try {
      url = new URL(this.availabilityUrl, window.location.origin);
    } catch (_e) {
      clearTimeout(timer);
      this.cutoffRefetchPending = false;
      this.setLoading(false);
      this.renderStateA();
      return;
    }
    url.searchParams.set('postcode', postcode);
    if (suburb) url.searchParams.set('suburb', suburb);
    else url.searchParams.delete('suburb');
    if (this.productId) url.searchParams.set('productId', this.productId);

    var opts = { method: 'GET', credentials: 'omit', headers: { Accept: 'application/json' } };
    if (this.abortController) opts.signal = this.abortController.signal;

    fetch(url.toString(), opts)
      .then(function (res) {
        if (!res.ok) throw new Error('bad status');
        return res.json();
      })
      .then(function (data) {
        clearTimeout(timer);
        if (timedOut) return;
        self.cutoffRefetchPending = false;
        self.setLoading(false);
        self.fetchedAt = Date.now();
        self.lastPayload = data || {};
        self.applyResponse(self.lastPayload, postcode, suburb, options || {});
      })
      .catch(function () {
        clearTimeout(timer);
        self.cutoffRefetchPending = false;
        self.setLoading(false);
        if (!soft) self.renderStateA();
      });
  };

  PdpShipping.prototype.applyResponse = function (data, postcode, suburb, options) {
    var fromEvent = !!(options && options.fromEvent);
    var state = String((data && data.state) || '').toLowerCase();
    if (!state && data && data.enabled === false) state = 'not_serviced';
    if (!state && data && Array.isArray(data.windows)) {
      state = data.windows.length ? 'ok' : 'empty';
    }

    if (state === 'excluded') {
      this.renderExcluded();
      return;
    }

    if (state === 'suburb_required') {
      this.postcode = padPostcode(data.postcode || postcode);
      this.renderSuburbRequired(this.postcode, data.suburbs || []);
      return;
    }

    if (state === 'not_serviced') {
      this.postcode = padPostcode(data.postcode || postcode);
      this.suburb = suburb || '';
      writeStorage(this.postcode, this.suburb, 'not_serviced');
      this.renderNotServiced(this.postcode, this.suburb);
      if (!fromEvent) this.broadcastLocation(this.postcode, this.suburb);
      this.broadcastAvailability(data);
      return;
    }

    this.postcode = padPostcode(data.postcode || postcode);
    this.suburb = suburb || '';
    var resolvedState = state === 'empty' || !(data.windows && data.windows.length) ? 'empty' : 'ok';
    writeStorage(this.postcode, this.suburb, resolvedState);
    if (!fromEvent) this.broadcastLocation(this.postcode, this.suburb);
    this.broadcastAvailability(data);

    if (state === 'empty' || !(data.windows && data.windows.length)) {
      this.renderEmptyWindows(data, this.postcode, this.suburb);
      return;
    }

    this.renderOk(data, this.postcode, this.suburb);
  };

  PdpShipping.prototype.renderHeader = function (postcode, suburb, showChange) {
    if (!this.headerEl) return;
    this.headerEl.hidden = false;
    this.headerEl.innerHTML = '';
    var text = document.createElement('span');
    text.className = 'pdp-ship__delivering-text';
    text.appendChild(document.createTextNode('Delivering to '));
    var pc = document.createElement('span');
    pc.className = 'pdp-ship__loc-strong';
    pc.textContent = postcode;
    text.appendChild(pc);
    var suburbDisplay = titleCaseSuburb(suburb);
    if (suburbDisplay) {
      text.appendChild(document.createTextNode(' '));
      var sub = document.createElement('span');
      sub.className = 'pdp-ship__loc-strong pdp-ship__loc-strong--suburb';
      sub.textContent = suburbDisplay;
      text.appendChild(sub);
    }
    this.headerEl.appendChild(text);
    if (showChange) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pdp-ship__text-link pdp-ship__text-link--muted';
      btn.textContent = this.changeLabel;
      var self = this;
      btn.addEventListener('click', function () {
        self.onChangeLocation();
      });
      this.headerEl.appendChild(btn);
    }
  };

  PdpShipping.prototype.hideHeader = function () {
    if (!this.headerEl) return;
    this.headerEl.hidden = true;
    this.headerEl.innerHTML = '';
  };

  PdpShipping.prototype.renderExcluded = function () {
    this.clearTick();
    this.hideHeader();
    this.hideActions();
    this.clearWindows();
    this.setWashed(false);
    this.setResolved(false);
    this.setBrandMode(false);
    this.setDetail('Not available for this product', true);
    this.setPrice('');
    this.setStatus('Standard and express shipping options.');
  };

  PdpShipping.prototype.renderStateA = function () {
    this.clearTick();
    this.hideHeader();
    this.clearWindows();
    this.setWashed(false);
    this.setResolved(false);
    this.setBrandMode(false);
    this.setDetail(this.sameDayTeaser, true);
    this.setPrice('');
    this.showOpenCheck();
    this.setStatus('Enter a postcode to check Same Day delivery.');
  };

  PdpShipping.prototype.renderSuburbRequired = function (postcode, suburbs) {
    this.clearTick();
    this.hideHeader();
    this.clearWindows();
    this.setWashed(false);
    this.setResolved(false);
    this.setBrandMode(false);
    this.setDetail(this.suburbPrompt.replace(/\[\[pc\]\]/g, postcode), true);
    this.setPrice('');
    if (this.detailEl) this.detailEl.hidden = false;
    if (this.openCheckBtn) this.openCheckBtn.hidden = true;
    if (this.inlineCheck) this.inlineCheck.hidden = true;
    if (this.postcodeInput) this.postcodeInput.value = postcode;
    if (this.suburbWrap) this.suburbWrap.hidden = false;
    if (this.suburbSelect) {
      this.suburbSelect.innerHTML = '';
      var placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = 'Select suburb';
      this.suburbSelect.appendChild(placeholder);
      (suburbs || []).forEach(function (name) {
        var opt = document.createElement('option');
        opt.value = name;
        opt.textContent = titleCaseSuburb(name);
        this.suburbSelect.appendChild(opt);
      }, this);
    }
    this.setStatus(this.suburbPrompt.replace(/\[\[pc\]\]/g, postcode));
  };

  PdpShipping.prototype.renderNotServiced = function (postcode, suburb) {
    this.clearTick();
    this.renderHeader(postcode, suburb, true);
    this.hideActions();
    this.clearWindows();
    this.setWashed(false);
    this.setResolved(false);
    this.setBrandMode(false);
    var msg = this.notServicedMessage.replace(/\[\[pc\]\]/g, postcode);
    this.setDetail(msg, true);
    this.setPrice('');
    this.setStatus(msg);
  };

  PdpShipping.prototype.renderEmptyWindows = function (data, postcode, suburb) {
    this.clearTick();
    this.renderHeader(postcode, suburb, true);
    this.hideActions();
    this.clearWindows();
    this.setWashed(false);
    this.setResolved(false);
    this.setBrandMode(false);
    var label =
      (data && (data.nextLabel || data.next_label || data.nextAvailable || data.next_available)) || '';
    if (!label && data && data.message) label = data.message;
    if (!label && data && Array.isArray(data.windows) && data.windows[0] && data.windows[0].label) {
      label = data.windows[0].label;
    }
    this.setDetail(label ? 'Next available: ' + label : 'Next available: check back soon', true);
    this.setPrice('');
    this.setStatus(this.detailEl ? this.detailEl.textContent : '');
  };

  PdpShipping.prototype.renderOk = function (data, postcode, suburb) {
    var windows = (data && data.windows) || [];
    this.renderHeader(postcode, suburb, true);
    this.hideActions();

    var price = (data && (data.sameDayPrice || data.price || data.same_day_price)) || this.sameDayPrice;
    this.setDetail('', true);
    this.renderWindows(data, windows);

    if (!this.activeWindows.length) {
      if ((windows && windows.length) || this.cutoffRefetchPending) {
        return;
      }
      this.renderEmptyWindows(data, postcode, suburb);
      return;
    }

    this.setWashed(true);
    this.setResolved(true);
    this.setBrandMode(true);
    this.setPrice(String(price), true);
    if (this.srSummaryEl) {
      var win0 = this.activeWindows[0];
      this.srSummaryEl.textContent = win0
        ? 'Same Day available for ' + String(price) + '. Earliest ' +
          windowLine(win0) + '.'
        : '';
    }
    this.startTick();
    this.setStatus('Same Day Delivery available. Choose your option at checkout.');
  };

  PdpShipping.prototype.startTick = function () {
    var self = this;
    this.clearTick();
    if (!this.activeWindows.length) return;
    this.tickTimer = setInterval(function () {
      if (!self.lastPayload) return;
      var source = (self.lastPayload.windows || []).slice();
      self.renderWindows(self.lastPayload, source);
      if (!self.activeWindows.length && !self.cutoffRefetchPending) {
        self.scheduleCutoffRefetch();
      }
    }, TICK_MS);
  };

  PdpShipping.prototype.clearTick = function () {
    if (this.tickTimer) {
      clearInterval(this.tickTimer);
      this.tickTimer = null;
    }
  };

  function initAll() {
    document.querySelectorAll('[data-pdp-shipping]').forEach(function (el) {
      if (el.__pdpShipping) return;
      el.__pdpShipping = new PdpShipping(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
